'use client';

import EthereumBlockNumber from '#/components/EthereumBlockNumber';
import EthereumHexString from '#/components/EthereumHexString';
import { Timestamp } from '#/components/timestamp';
import { Badge } from '#/components/ui/badge';
import { Button } from '#/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '#/components/ui/table';
import { CopyToClipboardButton } from '#/components/v2/copy-to-clipboard';
import { useTranslation } from '#/contexts/useTranslation';
import { useConfiguredBlockExplorer } from '#/data/wagmi/configured';
import { ethers } from 'ethers';
import { Translation } from 'intl-lib';
import { CheckCircle, ExternalLink, Loader2, XCircle } from 'lucide-react';
import Link from 'next/link';
import { ComponentProps } from 'react';
import { formatEther, Hex } from 'viem';
import {
  useBlock,
  useTransaction,
  useTransactionConfirmations,
  useWaitForTransactionReceipt,
} from 'wagmi';

interface Props {
  transactionHash?: Hex;
}

export function TransactionDetails({ transactionHash }: Props) {
  const translation = useTranslation(
    translation => translation.EthereumStaking
  );
  const blockExplorer = useConfiguredBlockExplorer();
  const { data: transaction } = useTransaction({
    hash: transactionHash,
  });
  const { data: confirmations } = useTransactionConfirmations({
    hash: transactionHash,
  });
  const { data: transactionReceipt } = useWaitForTransactionReceipt({
    hash: transactionHash,
  });
  const { data: block } = useBlock({
    blockNumber: transaction?.blockNumber,
    query: {
      enabled: transaction?.blockNumber != null,
    },
  });
  return (
    <div className="border rounded-md p-2">
      <Table>
        <TableBody>
          <TableRow className="border-none">
            <TableCell className="font-medium">Transaction Hash</TableCell>
            <TableCell className="flex items-center gap-2">
              <Button
                variant="link"
                size="sm"
                className="px-0 font-mono underline decoration-dashed decoration-gray-300 underline-offset-4"
                asChild
              >
                <Link
                  href={`${blockExplorer?.url}/tx/${transactionHash}`}
                  target="_blank"
                  rel="noreferrer"
                  prefetch={false}
                >
                  <EthereumHexString value={transactionHash} />
                </Link>
              </Button>
              <CopyToClipboardButton
                text={transactionHash}
                variant="outline"
                size="icon"
                className="rounded-full"
              />
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Status</TableCell>
            <TableCell className="">
              {transactionReceipt?.status != null ? (
                transactionReceipt.status === 'reverted' ? (
                  <div className="rounded-md border border-red-300 bg-red-50 text-sm text-red-600 py-0.5 px-2 w-fit">
                    <XCircle className="mr-1 w-3 h-3 inline-block" />
                    {translation.TransactionDialog.FailStatus}
                  </div>
                ) : (
                  <div className="rounded-md border border-green-300 bg-green-50 text-sm text-green-600 py-0.5 px-2 w-fit">
                    <CheckCircle className="mr-1 w-3 h-3 inline-block" />
                    {translation.TransactionDialog.SuccessStatus}
                  </div>
                )
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Block</TableCell>
            <TableCell className="">
              {transaction?.blockNumber != null ? (
                <div className="flex gap-2 items-center">
                  <EthereumBlockNumber blockNumber={transaction.blockNumber} />
                  <Badge>{confirmations} Block Confirmations</Badge>
                </div>
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Timestamp</TableCell>
            <TableCell className="">
              {block?.timestamp != null ? (
                <Timestamp date={Number(block.timestamp) * 1000} />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
          <TableRow className="border-none">
            <TableCell className="font-medium">Transaction Fee</TableCell>
            <TableCell className="">
              {transaction &&
              transactionReceipt &&
              transaction.gasPrice &&
              transactionReceipt.gasUsed ? (
                `${ethers.utils.commify(
                  formatEther(transaction.gasPrice * transactionReceipt.gasUsed)
                )} ETH`
              ) : (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export function TransactionViewOnButton({
  transactionHash,
  ...props
}: Props & ComponentProps<typeof Button>) {
  const blockExplorer = useConfiguredBlockExplorer();
  return (
    <Button {...props} type="button" asChild>
      <Link
        href={`${blockExplorer?.url}/tx/${transactionHash}`}
        target="_blank"
        rel="noreferrer"
        prefetch={false}
      >
        <ExternalLink />
        <Translation
          path="global.noun.view_on_button_label"
          params={blockExplorer}
        />
      </Link>
    </Button>
  );
}
